import { create } from 'zustand';
import { searchFiles, type SearchMatch } from '@/services/search.service';

/* ================================================================== */
/*  Search Store — workspace-wide text search state                   */
/* ================================================================== */

interface SearchState {
  query: string;
  results: SearchMatch[];
  totalMatches: number;
  filesSearched: number;
  truncated: boolean;
  isSearching: boolean;
  caseSensitive: boolean;
  useRegex: boolean;
  includePattern: string;
  error: string | null;
  /** Grouped results: path → matches */
  groupedResults: Map<string, SearchMatch[]>;

  setQuery: (query: string) => void;
  setCaseSensitive: (v: boolean) => void;
  setUseRegex: (v: boolean) => void;
  setIncludePattern: (v: string) => void;
  search: (workspaceId: string) => Promise<void>;
  clear: () => void;
}

function groupByFile(matches: SearchMatch[]): Map<string, SearchMatch[]> {
  const map = new Map<string, SearchMatch[]>();
  for (const m of matches) {
    const list = map.get(m.path) ?? [];
    list.push(m);
    map.set(m.path, list);
  }
  return map;
}

export const useSearchStore = create<SearchState>((set, get) => ({
  query: '',
  results: [],
  totalMatches: 0,
  filesSearched: 0,
  truncated: false,
  isSearching: false,
  caseSensitive: false,
  useRegex: false,
  includePattern: '',
  error: null,
  groupedResults: new Map(),

  setQuery: (query) => set({ query }),
  setCaseSensitive: (v) => set({ caseSensitive: v }),
  setUseRegex: (v) => set({ useRegex: v }),
  setIncludePattern: (v) => set({ includePattern: v }),

  search: async (workspaceId: string) => {
    const { query, caseSensitive, useRegex, includePattern } = get();
    if (!query.trim()) {
      set({ results: [], totalMatches: 0, filesSearched: 0, truncated: false, groupedResults: new Map(), error: null });
      return;
    }

    set({ isSearching: true, error: null });

    try {
      const result = await searchFiles(workspaceId, query, {
        caseSensitive,
        regex: useRegex,
        include: includePattern || undefined,
      });

      set({
        results: result.matches,
        totalMatches: result.totalMatches,
        filesSearched: result.filesSearched,
        truncated: result.truncated,
        groupedResults: groupByFile(result.matches),
        isSearching: false,
      });
    } catch (err: unknown) {
      set({
        isSearching: false,
        error: err instanceof Error ? err.message : 'Search failed',
      });
    }
  },

  clear: () => set({
    query: '',
    results: [],
    totalMatches: 0,
    filesSearched: 0,
    truncated: false,
    groupedResults: new Map(),
    error: null,
  }),
}));
